import {
	Banknote,
	Calendar,
	ClipboardCheck,
	Coffee,
	FileText,
	Key,
	type LucideIcon,
	PiggyBank,
	Plane,
	Search,
	Shield,
	TrendingUp,
	Truck,
	Zap,
} from "lucide-react";
import ownHomeImg from "shared/assets/inspiration/ins-001-own-home.jpg";
import earlyFreedomImg from "shared/assets/inspiration/ins-022-early-freedom.jpg";
import type { ObjPathProxy } from "ts-object-path";
import { InspirationTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { SliderBounds } from "web_myplann/Inspiration/Types/SliderBounds";
import type {
	EarlyFreedomAnchorKey,
	EarlyFreedomScenarioKey,
} from "web_myplann/Inspiration/Utils/earlyFreedomMath";
import { CurrencyCode } from "web_myplann/Shared/constants";

export const OWN_HOME_RENT_FIELD: SliderBounds = {
	min: 5_000,
	max: 40_000,
	step: 500,
	softMin: 5_000,
	softMax: 35_000,
};
export const OWN_HOME_RENT_FALLBACK = 18_000;

export const OWN_HOME_INCOME_FIELD: SliderBounds = {
	min: 20_000,
	max: 200_000,
	step: 1_000,
	softMin: 20_000,
	softMax: 200_000,
};
export const OWN_HOME_INCOME_FALLBACK = 55_000;

export const OWN_HOME_SAVINGS_FIELD: SliderBounds = {
	min: 0,
	max: 2_000_000,
	step: 10_000,
	softMax: 3_000_000,
};
export const OWN_HOME_SAVINGS_FALLBACK = 0;

export const OWN_HOME_DOWN_PAYMENT_SAVING = 8_000;

export const OWN_HOME_EXPENSES_FIELD: SliderBounds = {
	min: 5_000,
	max: 150_000,
	step: 1_000,
	softMin: 15_000,
	softMax: 150_000,
};
export const OWN_HOME_EXPENSES_FALLBACK = 35_000;

export const EARLY_FREEDOM_TARGET_MIN = 10_000;
export const EARLY_FREEDOM_TARGET_MAX = 200_000;
export const EARLY_FREEDOM_TARGET_STEP = 1_000;
export const EARLY_FREEDOM_TARGET_FALLBACK = 45_000;

export const EARLY_FREEDOM_RENT_MIN = 0;
export const EARLY_FREEDOM_RENT_MAX = 200_000;
export const EARLY_FREEDOM_RENT_STEP = 500;

export const EARLY_FREEDOM_SAVING_MIN = 0;
export const EARLY_FREEDOM_SAVING_MAX = 200_000;
export const EARLY_FREEDOM_SAVING_STEP = 1_000;
export const EARLY_FREEDOM_SAVING_FALLBACK = 5_000;

export const EARLY_FREEDOM_ANCHOR_MODEST = 25_000;
export const EARLY_FREEDOM_ANCHOR_COMFORT = 45_000;
export const EARLY_FREEDOM_ANCHOR_LUXURY = 80_000;

const TimelineResources = Resources.Inspiration.OwnHome.Result.Timeline;

type OwnHomeTimelineStep = {
	icon: LucideIcon;
	step: ObjPathProxy<
		MyPlannResourceDictionary,
		{ title: string; duration: string; desc: string }
	>;
};

export const OWN_HOME_TIMELINE_STEPS: OwnHomeTimelineStep[] = [
	{ icon: FileText, step: TimelineResources.prep },
	{ icon: Search, step: TimelineResources.search },
	{ icon: ClipboardCheck, step: TimelineResources.mortgage },
	{ icon: Key, step: TimelineResources.approval },
	{ icon: Truck, step: TimelineResources.moving },
];

const AnchorResources = Resources.Inspiration.EarlyFreedom.Anchors;
const PhaseResources = Resources.Inspiration.EarlyFreedom.Phases;

export type EarlyFreedomAnchorOption = {
	key: EarlyFreedomAnchorKey;
	icon: LucideIcon;
	amount: number;
	copy: ObjPathProxy<
		MyPlannResourceDictionary,
		{ label: string; description: string }
	>;
};

export const EARLY_FREEDOM_LIFESTYLE_ANCHORS: EarlyFreedomAnchorOption[] = [
	{
		key: "modest",
		icon: Coffee,
		amount: EARLY_FREEDOM_ANCHOR_MODEST,
		copy: AnchorResources.modest,
	},
	{
		key: "comfort",
		icon: Banknote,
		amount: EARLY_FREEDOM_ANCHOR_COMFORT,
		copy: AnchorResources.comfort,
	},
	{
		key: "luxury",
		icon: Plane,
		amount: EARLY_FREEDOM_ANCHOR_LUXURY,
		copy: AnchorResources.luxury,
	},
];

export const EARLY_FREEDOM_PHASES: {
	icon: LucideIcon;
	step: ObjPathProxy<
		MyPlannResourceDictionary,
		{ title: string; duration: string; desc: string }
	>;
}[] = [
	{ icon: PiggyBank, step: PhaseResources.diversify },
	{ icon: TrendingUp, step: PhaseResources.build },
	{ icon: Shield, step: PhaseResources.approaching },
	{ icon: Zap, step: PhaseResources.living },
];

export const EARLY_FREEDOM_SCENARIO_LABELS: Record<
	EarlyFreedomScenarioKey,
	ObjPathProxy<MyPlannResourceDictionary, string>
> = {
	conservative: Resources.Inspiration.EarlyFreedom.scenarioConservative,
	balanced: Resources.Inspiration.EarlyFreedom.scenarioBalanced,
	dynamic: Resources.Inspiration.EarlyFreedom.scenarioDynamic,
};

const ReadyStepResources = Resources.Inspiration.EarlyFreedom.ReadySteps;

export type EarlyFreedomReadyStep = {
	icon: LucideIcon;
	title: ObjPathProxy<MyPlannResourceDictionary, string>;
	subtitle: ObjPathProxy<MyPlannResourceDictionary, string>;
};

export const EARLY_FREEDOM_READY_STEPS: EarlyFreedomReadyStep[] = [
	{
		icon: Calendar,
		title: ReadyStepResources.drawdown.title,
		subtitle: ReadyStepResources.drawdown.subtitle,
	},
	{
		icon: Shield,
		title: ReadyStepResources.portfolio.title,
		subtitle: ReadyStepResources.portfolio.subtitle,
	},
];

export const INSPIRATION_CURRENCY = CurrencyCode.CZK;

export type InspirationCard = {
	id: InspirationTypeEnum;
	titleKey: keyof typeof Resources.Dashboard.Inspiration.Cards;
	image: string;
};

export const INSPIRATION_CARDS: InspirationCard[] = [
	{ id: InspirationTypeEnum.OwnHome, titleKey: "ownHome", image: ownHomeImg },
	{
		id: InspirationTypeEnum.EarlyFreedom,
		titleKey: "earlyFreedom",
		image: earlyFreedomImg,
	},
];
